import { ReactNode } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { METRIC_CHART_HEIGHT } from '../chartConfig'
import { MetricSeries } from '../types'
import { ExceptionMetricChart } from './ExceptionMetricChart'
import { LegendItem } from './LegendItem'
import { SummaryStat } from './SummaryStat'

interface Props {
  title: string
  color: string
  countCaption: string
  tooltipLabel: string
  icon?: ReactNode
  info?: string
  series?: MetricSeries | null
  loading?: boolean
}

export const MetricCard = ({
  title,
  color,
  countCaption,
  tooltipLabel,
  icon,
  info,
  series,
  loading,
}: Props) => (
  <Paper
    variant='outlined'
    sx={{ p: 2.5, height: '100%', borderRadius: 2, borderTop: `3px solid ${color}` }}>
    <Stack direction='row' spacing={1} alignItems='center' mb={0.5}>
      {icon && <Box sx={{ color, display: 'flex' }}>{icon}</Box>}
      <Typography variant='subtitle1' fontWeight={600} sx={{ color }}>
        {title}
      </Typography>
      {info && (
        <Tooltip
          title={info}
          slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        </Tooltip>
      )}
    </Stack>

    <Typography variant='body2' color='primary.main' mb={2}>
      Daily number and % of total jobs
    </Typography>

    <Stack direction='row' spacing={4} mb={1.5}>
      <SummaryStat
        value={`${(series?.summaryCount ?? 0).toLocaleString()} of ${(
          series?.summaryTotal ?? 0
        ).toLocaleString()}`}
        caption={countCaption}
        loading={loading}
      />
      <SummaryStat
        value={`${Number(series?.summaryPercentage ?? 0).toFixed(2)}%`}
        caption='% of total jobs'
        loading={loading}
      />
    </Stack>

    <Stack direction='row' spacing={2} mb={1} alignItems='center'>
      <LegendItem label='% of total jobs' swatch='line' color={color} />
      <LegendItem label='Number of jobs' swatch='bar' color={color} />
    </Stack>

    {loading ? (
      <Skeleton variant='rectangular' height={METRIC_CHART_HEIGHT} sx={{ borderRadius: 1 }} />
    ) : (
      <ExceptionMetricChart daily={series?.daily ?? []} color={color} tooltipLabel={tooltipLabel} />
    )}
  </Paper>
)
